import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { InstallPath, installPath } from '../shared/utils/install-app';
import { trackConversion } from './conversionEvents';

// Chromium-only event; not in the DOM typings.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const VISITS_KEY = 'install-app:visits';
const SESSION_KEY = 'install-app:session';
const DISMISSED_KEY = 'install-app:dismissed-at';

type InstallState = {
  path: InstallPath;
  visits: number;
  dismissedAt: number | null;
  guideOpen: boolean;
  install: () => Promise<void>;
  dismiss: () => void;
  closeGuide: () => void;
};

const InstallContext = createContext<InstallState | null>(null);

const readNumber = (key: string): number | null => {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

export function InstallAppProvider({ children }: { children: React.ReactNode }) {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState(false);
  const [path, setPath] = useState<InstallPath>('unsupported');
  const [visits, setVisits] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const sync = () => setStandalone(media.matches || (navigator as any).standalone === true);
    sync();
    media.addEventListener?.('change', sync);

    const onPrompt = (event: Event) => {
      // Keep the browser's mini-infobar quiet; our button triggers the same dialog.
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setPrompt(null);
      setStandalone(true);
      trackConversion('app_installed');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    try {
      let count = readNumber(VISITS_KEY) || 0;
      if (!sessionStorage.getItem(SESSION_KEY)) {
        count += 1;
        sessionStorage.setItem(SESSION_KEY, '1');
        localStorage.setItem(VISITS_KEY, String(count));
      }
      setVisits(count);
    } catch {
      setVisits(1);
    }
    setDismissedAt(readNumber(DISMISSED_KEY));

    return () => {
      media.removeEventListener?.('change', sync);
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    setPath(installPath({
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints,
      standalone,
      hasPrompt: !!prompt,
    }));
  }, [standalone, prompt]);

  const install = useCallback(async () => {
    if (prompt) {
      trackConversion('app_install_prompt');
      await prompt.prompt();
      const choice = await prompt.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
      // A prompt can only be used once; Chrome fires a fresh event if the user may try again.
      setPrompt(null);
      if (choice.outcome === 'accepted') {
        // Hide offers right away; `appinstalled` can arrive a few seconds later.
        setStandalone(true);
        trackConversion('app_install_accepted');
      }
      return;
    }
    trackConversion('app_install_guide');
    setGuideOpen(true);
  }, [prompt]);

  const dismiss = useCallback(() => {
    const now = Date.now();
    setDismissedAt(now);
    try { localStorage.setItem(DISMISSED_KEY, String(now)); } catch { /* optional */ }
  }, []);

  const closeGuide = useCallback(() => setGuideOpen(false), []);

  const value = useMemo(
    () => ({ path, visits, dismissedAt, guideOpen, install, dismiss, closeGuide }),
    [path, visits, dismissedAt, guideOpen, install, dismiss, closeGuide],
  );
  return <InstallContext.Provider value={value}>{children}</InstallContext.Provider>;
}

export function useInstallApp(): InstallState {
  const context = useContext(InstallContext);
  if (!context) throw new Error('useInstallApp must be used inside InstallAppProvider');
  return context;
}
